import { Button } from '@/components/Button';
import SettingsIcon from '@/components/CardWrapper/assets/icon_setting_linear.svg?react';
import Dropdown from '@/components/Dropdown';
import Modal from '@/components/Modal';
import ModalHeader from '@/components/Modal/ModalHeader';
import { TRADE_OVERLAY_TYPE } from '@/constants/trade';
import { useEarnStore } from '@/features/earn/store';
import { resetFormByChainId } from '@/features/trade/actions';
import { useTradeStore } from '@/features/trade/store';
import { useAppDispatch } from '@/hooks';
import { useChainId, useUserAddr } from '@/hooks/web3/useChain';
import EarnForm from '@/pages/earn/EarnCard/EarnForm';
import TradeAdjust from '@/pages/trade/TradeCard/TradeForm/TradeAdjust';
import TradeClose from '@/pages/trade/TradeCard/TradeForm/TradeClose';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMediaQueryDevice } from '../MediaQuery';
import SettingsContent from './SettingsContent';
import './index.less';

interface TradeAreaOverlayModalProps {
  cleanupOnUnmount?: boolean;
}

const TradeAreaOverlayModal: React.FC<TradeAreaOverlayModalProps> = ({ cleanupOnUnmount = false }) => {
  const { showTradeAreaOverlay, setShowTradeAreaOverlay, setClosePositionPairSymbol } = useTradeStore();
  const [isSettingsView, setIsSettingsView] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const chainId = useChainId();
  const userAddr = useUserAddr();
  const { deviceType, isMobile } = useMediaQueryDevice();
  const { setCurrentRangeId } = useEarnStore();

  const isShowSettingBtn = useMemo(() => {
    if (!showTradeAreaOverlay) {
      return false;
    }
    const showSetttingTypes = [
      TRADE_OVERLAY_TYPE.CLOSE_POSITION,
      TRADE_OVERLAY_TYPE.ADD_LIQ,
      TRADE_OVERLAY_TYPE.REMOVE_LIQ,
    ];
    return showSetttingTypes.includes(showTradeAreaOverlay);
  }, [showTradeAreaOverlay]);

  const handleClose = useCallback(() => {
    if (isSettingsView) {
      setIsSettingsView(false);
    } else {
      setShowTradeAreaOverlay(undefined);
      setClosePositionPairSymbol(undefined);
      chainId &&
        dispatch(
          resetFormByChainId({
            chainId,
          }),
        );
      setCurrentRangeId('');
    }
  }, [isSettingsView, setShowTradeAreaOverlay, setClosePositionPairSymbol, chainId, dispatch, setCurrentRangeId]);

  const handleSettingsClick = () => {
    setIsSettingsView(true);
  };

  useEffect(() => {
    return () => {
      if (cleanupOnUnmount) {
        setShowTradeAreaOverlay(undefined);
        setClosePositionPairSymbol(undefined);
      }
    };
  }, [cleanupOnUnmount, setShowTradeAreaOverlay, setClosePositionPairSymbol]);

  useEffect(() => {
    return () => {
      if (cleanupOnUnmount && showTradeAreaOverlay) {
        if (userAddr && chainId) {
          setShowTradeAreaOverlay(undefined);
          setClosePositionPairSymbol(undefined);
          dispatch(resetFormByChainId({ chainId }));
        }
      }
    };
  }, [userAddr, cleanupOnUnmount, showTradeAreaOverlay, chainId, dispatch, setClosePositionPairSymbol]);

  const overlayTitle = useMemo(() => {
    if (isSettingsView || showTradeAreaOverlay === TRADE_OVERLAY_TYPE.SETTING) {
      return 'Settings';
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION) {
      return t('common.closeP');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN) {
      return t('common.adjustM');
    }
    if (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ) {
      return t('common.earn.removeLiq');
    }
    return '';
  }, [isSettingsView, showTradeAreaOverlay, t]);

  const header = useMemo(() => {
    // For Modal, we always show settings if applicable?
    // In original code:
    /*
    const isPopupSettingsType =
      !isMobile &&
      (showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION || ...);
    */
    // Here we are likely in the "PopupSettingsType" scenario (Modal).

    const isPopupSettingsType = true; // Since this is the Modal component

    const settingButton = (
      <Button
        className="trade-area-overlay-setting"
        type="text"
        icon={<SettingsIcon className="syn-icon" />}
        onClick={isPopupSettingsType ? undefined : handleSettingsClick}
        size="small"
      />
    );

    return (
      <ModalHeader
        title={overlayTitle}
        onClose={handleClose}
        rightContent={
          isShowSettingBtn &&
          !isSettingsView &&
          (isPopupSettingsType ? (
            <Dropdown
              open={isDropdownOpen}
              onOpenChange={setIsDropdownOpen}
              trigger={['click']}
              placement="bottomRight"
              withPadding={false}
              dropdownRender={() => (
                <div className="trade-area-overlay-settings-dropdown">
                  <SettingsContent onSave={() => setIsDropdownOpen(false)} />
                </div>
              )}>
              {settingButton}
            </Dropdown>
          ) : (
            settingButton
          ))
        }
      />
    );
  }, [overlayTitle, handleClose, isShowSettingBtn, isSettingsView, isDropdownOpen]);

  const content = useMemo(
    () => (
      <div className={`trade-area-overlay-content`}>
        {isSettingsView ? (
          <SettingsContent
            onSave={() => {
              setIsSettingsView(false);
            }}
          />
        ) : (
          <>
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION && <TradeClose onClose={handleClose} />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN && <TradeAdjust onClose={handleClose} />}
            {showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ && (
              <EarnForm onClose={handleClose} isRemove={true} />
            )}
          </>
        )}
      </div>
    ),
    [isSettingsView, showTradeAreaOverlay, handleClose],
  );

  // Check types supported by Modal
  const isModalType =
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION ||
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN ||
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ;

  if (!isModalType || isMobile) {
    return null;
  }

  return (
    <Modal
      open={!!showTradeAreaOverlay}
      className={classNames(`trade-area-overlay-modal`, deviceType)}
      onCancel={handleClose}
      header={header}
      width={480}
      closable={false}
      footer={null}>
      {content}
    </Modal>
  );
};

export const shouldRenderTradeAreaOverlayModal = (showTradeAreaOverlay: string | undefined, isMobile: boolean) => {
  if (!showTradeAreaOverlay) return false;
  const isModalType =
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.CLOSE_POSITION ||
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.ADJUST_MARGIN ||
    showTradeAreaOverlay === TRADE_OVERLAY_TYPE.REMOVE_LIQ;
  return isModalType && !isMobile;
};

export default TradeAreaOverlayModal;
